/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { type WidenImageValue } from '@/components/widen-image';
import { widenVideoFileUrl, type WidenVideoValue } from '@/components/hero';
import { defaultSettingsProps, type DefaultSettingsValue } from '@/lib/page-designer/default-settings';
import HtmlFragment from '@/components/html-fragment';

type MediaType = 'image' | 'video';
type Alignment = 'left' | 'right';

const MEDIA_TYPES = ['image', 'video'] as const;
const ALIGNMENTS = ['left', 'right'] as const;

function normalize<T extends string>(value: string | undefined, allowed: readonly T[], fallbackValue: T): T {
    return value && (allowed as readonly string[]).includes(value) ? (value as T) : fallbackValue;
}

/* v8 ignore start - decorators are exercised by the generate-cartridge step, not unit tests */
@Component('contentContainer', {
    name: 'Content Container',
    description:
        'Full-width band pairing contained Widen media (image or video, sized to fit its column, uncropped) with rich-text copy. Default Settings sets the light/dark theme; the alignment option flips the media to the left or right.',
    group: '2_BlackMagic_Assets',
})
@RegionDefinition([])
export class ContentContainerMetadata {
    @AttributeDefinition({
        id: 'settings',
        name: 'Default Settings',
        description: 'Theme (light/dark), CSS class, and ID applied to the full-width outer element.',
        type: 'custom',
        editorDefinition: { type: 'inline.defaultSettings' },
    })
    settings?: DefaultSettingsValue;

    @AttributeDefinition({
        id: 'mediaType',
        name: 'Media Type',
        description: 'Whether the media slot shows the image or the video.',
        type: 'enum',
        values: ['image', 'video'],
        defaultValue: 'image',
    })
    mediaType?: string;

    @AttributeDefinition({
        id: 'image',
        name: 'Image',
        description: 'Widen DAM image (used when Media Type = image).',
        type: 'custom',
        editorDefinition: {
            type: 'inline.widenImage',
            configuration: {
                showSizes: false,
                showImageAlignment: false,
                showQualitySlider: false,
                showGridCoordinate: false,
            },
        },
    })
    image?: WidenImageValue;

    @AttributeDefinition({
        id: 'video',
        name: 'Video',
        description: 'Widen DAM video (used when Media Type = video).',
        type: 'custom',
        editorDefinition: { type: 'inline.widenVideo' },
    })
    video?: WidenVideoValue;

    @AttributeDefinition({
        id: 'copy',
        name: 'Copy',
        description: 'Rich-text copy shown beside the media.',
        type: 'markup',
        required: true,
    })
    copy?: string;

    @AttributeDefinition({
        id: 'alignment',
        name: 'Media Alignment',
        description: 'Place the media on the left or the right of the copy.',
        type: 'enum',
        values: ['left', 'right'],
        defaultValue: 'left',
    })
    alignment?: string;
}
/* v8 ignore stop */

export interface ContentContainerProps {
    settings?: DefaultSettingsValue;
    mediaType?: string;
    image?: WidenImageValue;
    video?: WidenVideoValue;
    copy?: string;
    alignment?: string;
}

const layoutVariants = cva('flex items-center gap-3 md:gap-12', {
    variants: {
        alignment: {
            left: 'flex-row',
            right: 'flex-row-reverse',
        },
    },
    defaultVariants: { alignment: 'left' },
});

/**
 * Columns split 1/3 media : 2/3 copy on mobile, 1/2 : 1/2 on desktop (the copy column carries the
 * responsive grow). Each column's content hugs the center gap — the media is pushed to the inner
 * edge of its column, and the copy text aligns to the inner edge of its.
 */
const COLUMN_ALIGN = {
    left: { media: 'justify-end', copy: 'text-left' },
    right: { media: 'justify-start', copy: 'text-right' },
} as const;

/** Media keeps its natural size but never exceeds its column. */
const MEDIA_CLASS = 'block h-auto max-w-full';

/** Parse a Widen dimension string (e.g. `"1200"`) to a positive integer, or `undefined` if blank/invalid. */
function toDimension(value?: string): number | undefined {
    const parsed = Number.parseInt(value ?? '', 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function ContentMedia({ mediaType, image, video }: Pick<ContentContainerProps, 'mediaType' | 'image' | 'video'>) {
    const type = normalize<MediaType>(mediaType, MEDIA_TYPES, 'image');

    if (type === 'video') {
        const fileUrl = widenVideoFileUrl(video);
        if (!fileUrl) return <MediaPlaceholder>Add a Widen video embed code</MediaPlaceholder>;
        // Widen video values carry no dimensions, so reserve a 16:9 box (uncropped via object-contain)
        // to avoid the layout shift the image fix below addresses. Decorative (muted/looping) media.
        return (
            <video
                data-slot="content-container-media"
                className="block aspect-video w-full max-w-full object-contain"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                aria-hidden="true">
                <source src={fileUrl} type="video/mp4" />
            </video>
        );
    }

    const fileURL = image?.fileURL?.trim();
    const webpURL = image?.webpURL?.trim();
    if (!fileURL) return <MediaPlaceholder>Add a Widen image embed code</MediaPlaceholder>;
    // Reserve the image's box before it loads to prevent layout shift (CLS). The Widen inline editor
    // auto-captures the asset's natural dimensions into imageWidth/imageHeight; passing them as the
    // intrinsic width/height lets the browser compute the aspect-ratio and hold space, while
    // `h-auto max-w-full` keeps it responsive and uncropped. Falls back to no reservation (prior
    // behavior) when dimensions are absent — e.g. legacy instances authored before auto-capture, which
    // backfill on the next save in Page Designer. (UCR-2790)
    const width = toDimension(image?.imageWidth);
    const height = toDimension(image?.imageHeight);
    const dimensions = width && height ? { width, height } : {};
    return (
        <picture data-slot="content-container-media" className="max-w-full">
            {webpURL && <source srcSet={webpURL} type="image/webp" />}
            <img
                src={fileURL}
                alt={image?.alternativeText ?? ''}
                className={MEDIA_CLASS}
                loading="lazy"
                {...dimensions}
            />
        </picture>
    );
}

function MediaPlaceholder({ children }: { children: ReactNode }): ReactElement {
    return (
        <div className="flex aspect-video w-full items-center justify-center bg-muted text-sm text-muted-foreground">
            {children}
        </div>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function fallback(): ReactElement {
    return <div className="h-48 w-full animate-pulse bg-muted" />;
}

/**
 * Content Container (UCR-2702) — a full-width band with contained media (sized to fit its column,
 * uncropped) beside rich-text copy. The Default Settings editor drives the light/dark theme
 * (background + font color) on the outer element; the alignment option flips the media left/right.
 */
export default function ContentContainer({
    settings,
    mediaType,
    image,
    video,
    copy,
    alignment,
}: ContentContainerProps): ReactElement {
    const align = normalize<Alignment>(alignment, ALIGNMENTS, 'left');

    return (
        <section {...defaultSettingsProps(settings, 'w-full')} data-slot="content-container">
            <div className="section-container py-8 md:py-10">
                <div className={layoutVariants({ alignment: align })}>
                    <div className={cn('flex flex-1', COLUMN_ALIGN[align].media)}>
                        <ContentMedia mediaType={mediaType} image={image} video={video} />
                    </div>
                    {copy && (
                        <div
                            data-slot="content-container-copy"
                            className={cn('flex-[2] md:flex-1', COLUMN_ALIGN[align].copy)}>
                            <HtmlFragment
                                content={copy}
                                className="text-lg font-normal leading-snug md:text-[26px] md:font-semibold md:leading-[1.1] md:tracking-[-0.02em]"
                            />
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export type ContentContainerVariants = VariantProps<typeof layoutVariants>;
